import { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, CalendarEvent } from "@/components/ui";
import { useToast } from "@/components/ui";
import { meetingsService, type Meeting } from "@/services/api/meetings";

const CALENDAR_PAGE_SIZE = 20;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getString = (value: unknown): string | undefined => {
  if (typeof value !== "string" && typeof value !== "number") return undefined;

  const normalized = String(value).trim();
  return normalized || undefined;
};

const readString = (
  source: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined => (source ? getString(source[key]) : undefined);

const parseDate = (value?: string | null) => {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const addMinutes = (date: Date, minutes: number) => {
  const nextDate = new Date(date);
  nextDate.setMinutes(nextDate.getMinutes() + minutes);
  return nextDate;
};

const getLeadAttendee = (meeting: Meeting) => meeting.attendees?.lead?.[0];

const getLeadName = (meeting: Meeting) => {
  const lead = isRecord(meeting.lead) ? meeting.lead : undefined;
  const attendee = getLeadAttendee(meeting);

  return (
    readString(lead, "fullName") ??
    readString(lead, "full_name") ??
    getString(attendee?.name) ??
    undefined
  );
};

const getLeadEmail = (meeting: Meeting) => {
  const lead = isRecord(meeting.lead) ? meeting.lead : undefined;
  const attendee = getLeadAttendee(meeting);

  return readString(lead, "emailAddress") ?? getString(attendee?.email);
};

const getMeetingId = (meeting: Meeting, index: number) =>
  getString(meeting.meetingUID) ??
  getString(meeting.meetingId) ??
  `meeting-${index + 1}`;

const getResponseList = (response: any): Meeting[] => {
  const list = response?.attributes?.data;
  return Array.isArray(list) ? list : [];
};

const meetingToCalendarEvent = (
  meeting: Meeting,
  index: number,
): CalendarEvent | null => {
  const start = parseDate(meeting.startTime);
  if (!start) return null;

  const end = parseDate(meeting.endTime) ?? addMinutes(start, 30);
  const leadName = getLeadName(meeting);
  const leadEmail = getLeadEmail(meeting);
  const agentName = getString(meeting.aiAgentData?.display_name);
  const attendees = [leadName, leadEmail, agentName].filter(Boolean) as string[];

  return {
    id: getMeetingId(meeting, index),
    title: getString(meeting.subject) ?? "Booked Appointment",
    description: getString(meeting.description),
    start,
    end,
    type: "meeting",
    priority: "medium",
    attendees,
    location: getString(meeting.googleMeetLink),
    color: "blue",
    htmlLink: getString(meeting.googleMeetLink),
  };
};

export default function AppointmentCalendar() {
  const { error: toastError } = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMeetings = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await meetingsService.listing({
        pageNumber: 1,
        pageSize: CALENDAR_PAGE_SIZE,
      });

      setMeetings(getResponseList(response));
    } catch (error: any) {
      toastError(error?.message || "Failed to load booked appointments");
      setMeetings([]);
    } finally {
      setIsLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const events = useMemo<CalendarEvent[]>(
    () =>
      meetings
        .map(meetingToCalendarEvent)
        .filter((event): event is CalendarEvent => Boolean(event)),
    [meetings],
  );

  return (
    <div className="p-6">
      {isLoading && (
        <p className="mb-3 text-sm text-gray-500">Loading appointments...</p>
      )}
      <Calendar
        events={events}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        onEventClick={(event) => console.log(event)}
        onDateSelect={(date) => console.log(date)}
      />
    </div>
  );
}
